import React from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text'
import ColorPill from 'core-system/Pill/ColorPill'
import Box from '../components/Box'

interface Worksite {
  name: string
  employeeCount: number
  isCompliant: boolean
}

interface ComplianceRequirement {
  text: string
}

interface ComplianceCardProps {
  areaName: string
  totalEmployees: number
  totalSites: number
  compliantSites: number
  worksites: Worksite[]
  complianceRequirements: ComplianceRequirement[]
}

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
`

const TitleSection = styled.div`
  flex: 1;
`

const MetricsSection = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
`

const MetricCard = styled.div`
  background-color: ${(props) => props.theme.palette.grey.grey100};
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
  flex: 1;
`

const MetricValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${(props) => props.theme.palette.text.primary};
  margin-bottom: 0.25rem;
`

const MetricLabel = styled.div`
  font-size: 0.875rem;
  color: ${(props) => props.theme.palette.text.secondary};
`

const SectionTitle = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: ${(props) => props.theme.palette.text.primary};
  margin-bottom: 0.5rem;
`

const WorksiteItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background-color: ${(props) => props.theme.palette.grey.grey100};
  border-radius: 8px;
  margin-bottom: 0.75rem;
`

const WorksiteInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
`

const StatusDot = styled.div<{ isCompliant: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) =>
    props.isCompliant
      ? props.theme.palette.accent.teal500
      : props.theme.palette.primary.pink500};
`

const WorksiteDetails = styled.div`
  flex: 1;
`

const WorksiteName = styled.div`
  font-weight: 500;
  color: ${(props) => props.theme.palette.text.primary};
  margin-bottom: 0.25rem;
`

const EmployeeCount = styled.div`
  font-size: 0.875rem;
  color: ${(props) => props.theme.palette.text.secondary};
`

const RequirementsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`

const RequirementItem = styled.li`
  padding: 0.15rem 0;
  font-size: 0.875rem;
  color: ${(props) => props.theme.palette.text.secondary};
  position: relative;
  padding-left: 1.25rem;

  &:before {
    content: '•';
    position: absolute;
    left: 0;
    color: ${(props) => props.theme.palette.text.primary};
  }
`

const ComplianceCard: React.FC<ComplianceCardProps> = ({
  areaName,
  totalEmployees,
  totalSites,
  compliantSites,
  worksites,
  complianceRequirements,
}) => {
  const isOverallCompliant = compliantSites >= totalSites / 2

  return (
    <Box style={{ padding: '1.25rem' }}>
      <HeaderSection>
        <TitleSection>
          <Text variant='action1' style={{ marginBottom: '0.5rem' }}>
            {areaName}
          </Text>
        </TitleSection>
        <ColorPill
          color={isOverallCompliant ? 'green' : 'pink'}
          text={`${compliantSites}/${totalSites} Compliant`}
        />
      </HeaderSection>

      <MetricsSection>
        <MetricCard>
          <MetricValue>{totalEmployees}</MetricValue>
          <MetricLabel>Total Employees</MetricLabel>
        </MetricCard>
        <MetricCard>
          <MetricValue>{totalSites}</MetricValue>
          <MetricLabel>Total Sites</MetricLabel>
        </MetricCard>
      </MetricsSection>

      <SectionTitle>Worksites in Zone:</SectionTitle>
      {worksites.map((worksite, index) => (
        <WorksiteItem key={index}>
          <WorksiteInfo>
            <StatusDot isCompliant={worksite.isCompliant} />
            <WorksiteDetails>
              <WorksiteName>{worksite.name}</WorksiteName>
              <EmployeeCount>{worksite.employeeCount} employees</EmployeeCount>
            </WorksiteDetails>
          </WorksiteInfo>
          <ColorPill
            color={worksite.isCompliant ? 'green' : 'pink'}
            text={worksite.isCompliant ? 'Compliant' : 'Non-Compliant'}
          />
        </WorksiteItem>
      ))}

      <SectionTitle style={{ marginTop: '2rem' }}>
        Compliance Requirements:
      </SectionTitle>
      <RequirementsList>
        {complianceRequirements.map((requirement, index) => (
          <RequirementItem key={index}>{requirement.text}</RequirementItem>
        ))}
      </RequirementsList>
    </Box>
  )
}

export default ComplianceCard
